import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCloudArrowUp } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import axiosInstance from '../../../axiosSetup';
import UploadIndicator from '../../../components/common/UploadIndicator';

interface UploadFileItem {
  file: File;
}

interface DropzoneThingNewProps {
  files: any[];
  fulfilled: (
    rowNumber: string | number,
    fileName: string,
    tempFilePath: string,
    tempFileName: string
  ) => void;
  failed: (rowNumber: string | number) => void;
  pending: (rowNumber: string | number, loading: number) => void;
  clear: (rowNumber: string | number) => void;
  selected: (fileName: string) => string | number;
  acceptedTypes?: Record<string, string[]>;
  maxFiles?: number;
  maxFileSize?: number;
  maxFileNameLength?: number;
  disabled?: boolean;
  uploadEndpoint?: string;
  className?: string;
  placeholder?: string;
  dropPlaceholder?: string;
  showFileList?: boolean;
  allowMultiple?: boolean;
  invalid?: boolean;
  errorMsg?: string;
  getDocumentLink?: (file: any) => string | undefined;
  openDocument?: (file: any) => Promise<void>;
}

const DropzoneThingNew: React.FC<DropzoneThingNewProps> = ({
  files,
  fulfilled,
  failed,
  pending,
  clear,
  selected,
  acceptedTypes = { 'application/pdf': ['.pdf'] },
  maxFiles,
  maxFileSize,
  maxFileNameLength,
  disabled = false,
  uploadEndpoint = '/api/edo-rest-v1/v1/view/file',
  className = '',
  placeholder = "Drag 'n' drop files here, or click to select files",
  dropPlaceholder = 'Drop the files here ...',
  showFileList = true,
  allowMultiple = true,
  invalid = false,
  errorMsg = '',
  getDocumentLink,
  openDocument,
}) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const [fileRejections, setFileRejections] = useState<string[]>([]);

  const getAcceptedExtensions = (): string[] => {
    const extensions: string[] = [];
    Object.values(acceptedTypes).forEach((exts) => {
      extensions.push(...exts);
    });
    return extensions;
  };

  const getAcceptedMimeTypes = (): string[] => {
    return Object.keys(acceptedTypes);
  };

  const validateFile = (file: File): string | null => {
    if (maxFileSize && file.size > maxFileSize * 1024 * 1024) {
      return `File size exceeds ${maxFileSize}MB limit`;
    }

    if (maxFileNameLength && file.name.length > maxFileNameLength) {
      return `File name should not exceed ${maxFileNameLength} characters`;
    }

    const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
    const acceptedExts = getAcceptedExtensions();

    if (!acceptedExts.includes(fileExt)) {
      return `Only ${acceptedExts.join(', ')} files are allowed`;
    }

    return null;
  };

  const uploadFile = async (file: File, rowNumber: string | number) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axiosInstance.post(uploadEndpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress(progressEvent) {
          const percentCompleted = Math.min(
            99,
            Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total ?? 1)
            )
          );
          pending(rowNumber, percentCompleted);
        },
      });

      pending(rowNumber, 100);
      fulfilled(
        rowNumber,
        file.name,
        response.data.path,
        response.data.tempFileName
      );
    } catch (error) {
      console.error('File Upload Error: ', error);
      failed(rowNumber);
    }
  };

  const handleFiles = async (uploadItems: UploadFileItem[]) => {
    setFileRejections([]);

    if (maxFiles && files.length + uploadItems.length > maxFiles) {
      toast.error(
        `Maximum ${maxFiles} file${maxFiles > 1 ? 's' : ''} allowed`,
        { style: { color: '#fff' } }
      );
      return;
    }

    const rejections: string[] = [];
    const validFiles: UploadFileItem[] = [];

    uploadItems.forEach((item) => {
      const error = validateFile(item.file);
      if (error) {
        rejections.push(`${item.file.name}: ${error}`);
      } else {
        validFiles.push(item);
      }
    });

    if (rejections.length > 0) {
      setFileRejections(rejections);
      toast.error(rejections[0], { style: { color: '#fff' } });
      return;
    }

    validFiles.forEach((item) => {
      const rowNumber = selected(item.file.name);
      uploadFile(item.file, rowNumber);
    });
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragActive(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (disabled) return;

    const mappedFiles = Array.from(e.dataTransfer.files).map((file) => ({
      file,
    }));

    handleFiles(mappedFiles);
  };

  const handleClick = () => {
    if (disabled) return;

    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = allowMultiple;
    input.accept = getAcceptedMimeTypes().join(',');

    input.onchange = (evt: Event) => {
      const target = evt.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const mappedFiles = Array.from(target.files).map((file) => ({
          file,
        }));
        handleFiles(mappedFiles);
      }
    };

    input.click();
  };

  const removeFile = (rowNumber: string | number) => {
    clear(rowNumber);
  };

  return (
    <div className={`common-dropzone ${className}`}>
      <div
        className={`dropzone p-3 rounded ${isDragActive ? 'bg-light' : ''} ${
          disabled ? 'disabled-dropzone' : ''
        }`}
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          minHeight: 80,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderStyle: 'dashed',
          borderColor: '#80b0ef',
          borderWidth: '2px',
          opacity: disabled ? 0.6 : 1,
          transition: 'all 0.3s ease',
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <div className="text-center">
          <FontAwesomeIcon
            icon={faCloudArrowUp}
            className="text-primary mb-2"
            size="2x"
            style={{ opacity: 0.6 }}
          />
          <p className="mb-0 fs--1">
            {isDragActive ? dropPlaceholder : placeholder}
          </p>
          {maxFiles != null && maxFileSize != null && (
            <p className="mb-0 fs--2 text-muted mt-1">
              Max {maxFiles} file{maxFiles > 1 ? 's' : ''} • {maxFileSize}MB per
              file • {getAcceptedExtensions().join(', ')}
            </p>
          )}
        </div>
      </div>

      {showFileList && files.length > 0 && (
        <div className="mt-2">
          {files.map((file, index) => {
            const loading = file.loading ?? 0;
            const isUploading = loading > 0 && loading < 100;
            const documentLink = getDocumentLink?.(file);

            return (
              <div
                key={file.popAttId ?? file.rowNumber ?? index}
                className="d-flex align-items-center justify-content-between py-2 border-bottom"
              >
                <div className="d-flex align-items-center">
                  {loading !== 0 && (loading !== 100 || !documentLink) && (
                    <UploadIndicator
                      progress={loading}
                      fail={loading === -1}
                      style={{ marginRight: '20px' }}
                    />
                  )}
                  <span
                    style={{
                      width: 'max-content',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                    }}
                    className={`fs--1 ${documentLink ? 'text-primary cursor-pointer' : ''}`}
                    title={file.fileName}
                    onClick={async () => {
                      if (openDocument) {
                        await openDocument(file);
                        return;
                      }

                      if (!documentLink) return;
                      window.open(documentLink, '_blank');
                    }}
                  >
                    {file.fileName}
                  </span>
                </div>
                {!disabled && !isUploading && (
                  <FontAwesomeIcon
                    icon={faTimes}
                    className="text-danger cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(file.rowNumber);
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {fileRejections.length > 0 && (
        <div className="text-danger small mt-2">
          {fileRejections.map((rejection, idx) => (
            <div key={idx}>{rejection}</div>
          ))}
        </div>
      )}

      {invalid && (
        <div className="invalid-feedback d-block">
          {errorMsg || 'Please upload required files'}
        </div>
      )}
    </div>
  );
};

export default DropzoneThingNew;
